import {Box, Button} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import Errors from 'src/modules/shared/error/errors';
import FileUploader from 'src/modules/shared/fileUpload/fileUploader';
import {DisabledItem} from './shared/formItemWrappers';

function InputSignatureFormItem(props) {
  const signatureRef = useRef<any>();
  const lastRenderedValueRef = useRef<any>(null);
  const isDrawingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const getSignaturePad = () => {
    return signatureRef?.current?.getSignaturePad();
  };

  let {name, width, height, disabled, label, onChange, storage, signatureRefName} = props;
  width = window.innerWidth > 768 ? width * 0.7 : window.innerWidth * 0.76;
  height = width * 0.48;

  const {
    register,
    control,
    setValue,
    formState: {touchedFields, isSubmitted, errors},
  } = useFormContext();

  const watchValue: any = useWatch({name, control});

  useEffect(() => {
    register(name);
  }, []);

  useEffect(() => {
    if (signatureRefName) {
      register(signatureRefName);
    }
  }, [signatureRefName]);

  useEffect(() => {
    let active = true;
    const run = async () => {
      await signatureChanged(watchValue, () => active);
    };
    run();
    return () => {
      active = false;
    };
  }, [watchValue]);

  const toDataUrl = (url) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Cannot render image'));
            return;
          }
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = reject;
      image.src = url;
    });
  };

  const signatureChanged = async (value, isActive = () => true) => {
    const signaturePad = getSignaturePad();
    if (!signaturePad) return;

    if (isDrawingRef.current) {
      return;
    }

    if (!value) {
      signaturePad.clear();
      lastRenderedValueRef.current = null;
      return;
    }

    if (lastRenderedValueRef.current === value) {
      return;
    }

    let renderableValue = value;
    if (typeof value === 'string' && !value.startsWith('data:image')) {
      try {
        renderableValue = await toDataUrl(value);
      } catch (error) {
        renderableValue = value;
      }
    }

    if (!isActive()) return;

    try {
      signaturePad.fromDataURL(renderableValue, {width, height});
      lastRenderedValueRef.current = value;
    } catch (error) {
      // fallback: si no se puede cargar la imagen remota, dejamos la firma actual
    }
  };

  const clearCanvas = () => {
    const signaturePad = getSignaturePad();
    isDrawingRef.current = false;
    signaturePad.clear();
    lastRenderedValueRef.current = null;
    setValue(name, null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (signatureRefName) {
      setValue(signatureRefName, null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    onChange && onChange(null, null);
  };

  const getSignature = () => {
    const signaturePad = getSignaturePad();
    return signaturePad?.toDataURL('image/png', 1);
  };

  const dataUrlToFile = (dataUrl) => {
    const [meta, base64Data] = (dataUrl || '').split(',');
    const mime = meta?.match(/data:(.*?);base64/)?.[1] || 'image/png';
    const binary = atob(base64Data || '');
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const extension = mime.split('/')[1] || 'png';
    return new File([bytes], `signature-${Date.now()}.${extension}`, {type: mime});
  };

  const isDataUrl = (value) => {
    return typeof value === 'string' && value.startsWith('data:image');
  };

  const setSignature = async () => {
    isDrawingRef.current = false;
    const signaturePad = getSignaturePad();
    if (!signaturePad || signaturePad?.isEmpty?.()) {
      setValue(name, null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      if (signatureRefName) {
        setValue(signatureRefName, null, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      lastRenderedValueRef.current = null;
      onChange && onChange(null, null);
      return;
    }

    const newValue = getSignature();
    let signatureValue = newValue;
    let uploadedFile: any = null;

    if (storage && isDataUrl(newValue)) {
      try {
        setUploading(true);
        const file = dataUrlToFile(newValue);
        uploadedFile = await FileUploader.upload(file, {
          storage,
          image: true,
        });
        signatureValue = uploadedFile?.publicUrl || uploadedFile?.downloadUrl || newValue;
      } catch (error) {
        Errors.showMessage(error);
        signatureValue = newValue;
      } finally {
        setUploading(false);
      }
    }

    setValue(name, signatureValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    lastRenderedValueRef.current = signatureValue;
    if (signatureRefName && uploadedFile) {
      setValue(signatureRefName, uploadedFile, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    onChange && onChange(signatureValue, uploadedFile);
  };

  if (disabled) {
    return (
      <DisabledItem
        label={label}
        item={
          <Box sx={{border: '8px solid gray', borderRadius: '5px', width: 'min-content', mb: 1}}>
            {watchValue && <img alt={'signature'} style={{width, height}} src={watchValue} />}
          </Box>
        }
      />
    );
  }
  //return <CanvasExample />;
  //return <SignaturePadExample />;
  // return (
  //   <div style={{width, height, position: 'relative'}}>
  //     <div className="canvasWrapper500">
  //       <SignatureCanvas
  //         clearOnResize={false}
  //         ref={signatureRef}
  //         throttle={16}
  //         onEnd={() => {
  //           setValue(name, signatureRef?.current?.getSignaturePad().toDataURL('image/png', 1));
  //         }}
  //         penColor="black"
  //         canvasProps={{
  //           padding: 0,
  //           margin: 0,
  //           className: 'inputSigCanvas',
  //         }}
  //       />
  //     </div>
  //   </div>
  // );
  return (
    <Box display="flex" flexDirection="column" sx={{border: '8px solid gray', borderRadius: '5px', width: 'min-content', mb: 1}}>
      <Box>
        <Button onClick={clearCanvas} disabled={uploading}>
          {uploading ? 'Subiendo...' : 'Limpiar'}
        </Button>
      </Box>
      <Box
        sx={{
          width,
          height,
        }}>
        {!disabled ? (
          <SignatureCanvas
            clearOnResize={false}
            ref={signatureRef}
            minDistance={5}
            minWidth={0.5}
            maxWidth={2.5}
            throttle={16}
            onBegin={() => {
              isDrawingRef.current = true;
            }}
            onEnd={() => setSignature()}
            penColor="black"
            backgroundColor="white"
            //style={{width, height}}
            canvasProps={{
              padding: 0,
              margin: 0,
              width,
              height,
              // className: 'inputSigCanvas',
            }}
          />
        ) : (
          <img alt={'signature'} style={{width, height}} src={watchValue} />
        )}
      </Box>
    </Box>
  );
}

InputSignatureFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  firma: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  storage: PropTypes.object,
  signatureRefName: PropTypes.string,
  required: PropTypes.any,
};

export default InputSignatureFormItem;
